'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState, useSyncExternalStore } from 'react'
import {
  LayoutDashboard,
  Layers,
  Package,
  ShoppingBag,
  CreditCard,
  Settings,
  ChevronRight,
  Type,
  LifeBuoy,
  BookOpen,
  AlertTriangle,
  ExternalLink,
  ImageIcon,
  Truck,
  ShieldCheck,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { trpc } from '@/lib/trpc/client'
import { redirectTopLevel } from '@/lib/shopify-redirect'

function ScopeBanner() {
  const meQuery = trpc.shops.me.useQuery(undefined, { staleTime: 5 * 60 * 1000 })
  const scopeQuery = trpc.shops.checkScopes.useQuery(undefined, { staleTime: 5 * 60 * 1000 })

  if (scopeQuery.isLoading || scopeQuery.data?.hasDraftOrders !== false) return null

  const shopDomain = meQuery.data?.shopifyDomain ?? ''
  const reAuthUrl = shopDomain ? `https://yespersonalized.com/api/auth?shop=${shopDomain}` : '#'

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-3 text-sm">
      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
      <span className="text-amber-800 flex-1">
        <strong>Action required:</strong> The app needs to be reconnected to enable personalization charges.
        {' '}<strong>Click the button and approve the permissions to continue.</strong>
      </span>
      <button
        onClick={() => redirectTopLevel(reAuthUrl)}
        className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
      >
        Reconnect App
      </button>
    </div>
  )
}

const APP_BASE = '/app-embed-shopify'

/**
 * Sidebar nav. `badge` is a static label (e.g. 'Soon'). `countKey`
 * binds the item to a live count from `shops.sidebarCounts` — the
 * count badge only appears when the value is > 0, and uses a
 * different (coral) style to read as "needs your attention" rather
 * than informational.
 */
type NavItem = {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string
  external?: boolean
  countKey?: 'moderation' | 'orders' | 'support'
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: `${APP_BASE}/dashboard`, icon: LayoutDashboard },
  { name: 'Templates', href: `${APP_BASE}/templates`, icon: Layers },
  // Smart Templates — hidden for now; routes still exist under /app-embed-shopify/smart-templates
  { name: 'Products', href: `${APP_BASE}/products`, icon: Package },
  { name: 'Orders', href: `${APP_BASE}/orders`, icon: ShoppingBag, countKey: 'orders' },
  { name: 'Moderate', href: `${APP_BASE}/moderate`, icon: ShieldCheck, countKey: 'moderation' },
  { name: 'Fulfilment', href: `${APP_BASE}/fulfilment`, icon: Truck, badge: 'Soon' },
  { name: 'Fonts', href: `${APP_BASE}/fonts`, icon: Type },
  { name: 'Media', href: `${APP_BASE}/media`, icon: ImageIcon },
  { name: 'Billing', href: `${APP_BASE}/billing`, icon: CreditCard },
  { name: 'Settings', href: `${APP_BASE}/settings`, icon: Settings },
  { name: 'Docs', href: '/docs', icon: BookOpen, external: true },
  { name: 'Support', href: `${APP_BASE}/support`, icon: LifeBuoy, countKey: 'support' },
]

// ── Collapsed (icon-only) sidebar preference ─────────────────────────
// Remembered per browser. With no saved choice the sidebar starts collapsed on
// editor pages, where the canvas needs the width, and expanded elsewhere.
const NAV_PREF_KEY = 'yp_nav_collapsed'
const navPrefListeners = new Set<() => void>()
let navPrefMemory: boolean | null = null // used when localStorage is blocked

function readNavPref(): boolean | null {
  try {
    const v = localStorage.getItem(NAV_PREF_KEY)
    if (v === '1') return true
    if (v === '0') return false
  } catch {
    // storage blocked: fall through to the in-memory choice
  }
  return navPrefMemory
}

function writeNavPref(collapsed: boolean) {
  navPrefMemory = collapsed
  try { localStorage.setItem(NAV_PREF_KEY, collapsed ? '1' : '0') } catch { /* blocked */ }
  navPrefListeners.forEach((l) => l())
}

function subscribeNavPref(listener: () => void) {
  navPrefListeners.add(listener)
  return () => { navPrefListeners.delete(listener) }
}

interface AppShellProps {
  children: React.ReactNode
}

// Preserves the Shopify `?host=...` query param across client-side navigation.
// Without it, App Bridge falls back to window.location.origin as the postMessage
// target — see components/providers/AppBridgeProvider.tsx for context.
// Read via useSyncExternalStore: the server snapshot (and the hydration pass)
// is null, then the client value is applied, so SSR output never depends on it.
function readHostParam(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('host')
  if (fromUrl) return fromUrl
  try {
    return sessionStorage.getItem('shopify_host')
  } catch {
    // sessionStorage may be blocked
    return null
  }
}

const subscribeHostParam = () => () => {}

function useHostParam(): string | null {
  return useSyncExternalStore(subscribeHostParam, readHostParam, () => null)
}

function withHost(href: string, host: string | null): string {
  if (!host) return href
  if (href.startsWith('http')) return href
  if (href.includes('host=')) return href
  const sep = href.includes('?') ? '&' : '?'
  return `${href}${sep}host=${encodeURIComponent(host)}`
}

export function AppShell({ children }: AppShellProps) {
  const pathname = usePathname()
  const host = useHostParam()
  const [mobileOpen, setMobileOpen] = useState(false)
  const navPref = useSyncExternalStore(subscribeNavPref, readNavPref, () => null)
  // Editor pages fill exactly the space left by the shell and never scroll the
  // page, so the editor's Save/Publish bar can't scroll out of view.
  const isEditor = pathname.includes('/editor')
  const collapsed = navPref ?? isEditor
  // Hover/focus label for icon-only mode. Rendered fixed so the nav's scroll
  // container can't clip it.
  const [tip, setTip] = useState<{ name: string; top: number } | null>(null)

  // Live counts for the sidebar nav badges. Cheap (3 COUNT queries
  // server-side) and refetches on tab focus by default — that's the
  // right cadence for a "show me what needs my attention" feel.
  const sidebarCountsQuery = trpc.shops.sidebarCounts.useQuery(undefined, {
    staleTime: 30_000,
  })
  const counts = sidebarCountsQuery.data

  // Close the mobile drawer whenever the route changes — otherwise tapping a
  // nav link would leave the drawer open over the new page. Adjusted during
  // render against the previous pathname rather than in an effect.
  const [prevPathname, setPrevPathname] = useState(pathname)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname)
    setMobileOpen(false)
  }

  // Lock body scroll while the mobile drawer is open so the page underneath
  // doesn't move when the user pans.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (mobileOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [mobileOpen])

  // Find the current page name for the mobile top bar so the merchant always
  // knows where they are when the sidebar is collapsed.
  const currentPage = navigation.find((item) =>
    !('external' in item && item.external) &&
    (pathname === item.href || (item.href !== `${APP_BASE}/dashboard` && pathname.startsWith(item.href))),
  )

  const navList = (
    <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto overflow-x-hidden">
      {navigation.map((item) => {
        const isActive = !('external' in item && item.external) && (pathname === item.href || (item.href !== `${APP_BASE}/dashboard` && pathname.startsWith(item.href)))
        const Icon = item.icon
        const isExternal = 'external' in item && item.external
        const linkHref = isExternal ? item.href : withHost(item.href, host)

        const className = cn(
          // h-11 → 44px tap target (Apple HIG / WCAG minimum)
          'relative flex items-center gap-2.5 px-3 h-11 rounded-lg text-sm transition-colors group',
          collapsed && 'md:justify-center md:px-0',
          isActive
            ? 'bg-indigo-50 text-indigo-700 font-medium'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )
        const badge = item.badge
        // Live count for items bound to a count key. Falsy (0 or undefined)
        // = no badge — we don't want every nav item to glow at zero.
        const liveCount = item.countKey && counts ? counts[item.countKey] : 0
        const content = (
          <>
            <Icon className={cn('h-4 w-4 shrink-0 transition-colors', isActive ? 'text-indigo-600' : 'text-gray-500 group-hover:text-gray-600')} />
            <span className={cn('flex-1 truncate', collapsed && 'md:hidden')}>{item.name}</span>
            {collapsed && liveCount > 0 && (
              <span className="hidden md:block absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-rose-500" aria-hidden />
            )}
            {liveCount > 0 && (
              <span
                className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 leading-none shrink-0 tabular-nums min-w-[18px] text-center', collapsed && 'md:hidden')}
                aria-label={`${liveCount} ${liveCount === 1 ? 'item' : 'items'} need attention`}
              >
                {liveCount > 99 ? '99+' : liveCount}
              </span>
            )}
            {badge && (
              <span className={cn('text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 leading-none shrink-0 uppercase tracking-wide', collapsed && 'md:hidden')}>
                {String(badge)}
              </span>
            )}
            {isExternal && <ExternalLink className={cn('h-3 w-3 shrink-0 text-gray-300 group-hover:text-gray-400', collapsed && 'md:hidden')} />}
          </>
        )

        const tipLabel = liveCount > 0 ? `${item.name} (${liveCount > 99 ? '99+' : liveCount})` : item.name
        const showTip = (el: HTMLElement) => setTip({ name: tipLabel, top: el.getBoundingClientRect().top + 22 })
        const tipProps = collapsed
          ? {
              'aria-label': tipLabel,
              onMouseEnter: (e: React.MouseEvent<HTMLElement>) => showTip(e.currentTarget),
              onFocus: (e: React.FocusEvent<HTMLElement>) => showTip(e.currentTarget),
              onMouseLeave: () => setTip(null),
              onBlur: () => setTip(null),
            }
          : {}

        return isExternal ? (
          <a key={item.name} href={linkHref} target="_blank" rel="noopener noreferrer" className={className} {...tipProps}>
            {content}
          </a>
        ) : (
          <Link key={item.name} href={linkHref} className={className} {...tipProps}>
            {content}
          </Link>
        )
      })}
    </nav>
  )

  const sidebarFooter = (
    <div className={cn('p-3 border-t border-gray-100 flex items-center gap-1', collapsed && 'md:p-2 md:justify-center')}>
      <div className={cn('flex-1 px-3 py-2 text-xs text-gray-500', collapsed && 'md:hidden')}>
        v1.0.0 · YesPersonalized
      </div>
      <button
        type="button"
        onClick={() => { setTip(null); writeNavPref(!collapsed) }}
        aria-label={collapsed ? 'Expand menu' : 'Collapse menu to icons'}
        title={collapsed ? 'Expand menu' : 'Collapse menu to icons'}
        className="hidden md:flex h-9 w-9 shrink-0 rounded-lg items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
      >
        {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
      </button>
    </div>
  )

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* ── Mobile top bar ─────────────────────────────────────────────
        * Sits above the main content on viewports < md. Hidden at md+ where
        * the sidebar is permanently visible. */}
      <div
        className="md:hidden fixed inset-x-0 top-0 z-30 h-14 bg-white border-b border-gray-200 flex items-center px-3 gap-2"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <button
          type="button"
          aria-label="Open navigation"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
          className="h-10 w-10 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold text-gray-900 truncate flex-1">
          {currentPage?.name ?? 'YesPersonalized'}
        </span>
      </div>

      {/* ── Mobile drawer backdrop ─────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40 transition-opacity"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────
        * Mobile: fixed-position drawer that slides in from the left.
        * Desktop (md+): permanent flex column. */}
      <aside
        className={cn(
          'bg-white border-r border-gray-200 flex flex-col z-50 transition-transform duration-200 ease-out',
          // Desktop layout
          'md:relative md:shrink-0 md:translate-x-0',
          collapsed ? 'md:w-14' : 'md:w-56',
          // Mobile drawer
          'fixed inset-y-0 left-0 w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        {/* Mobile drawer header with close button */}
        <div className="md:hidden flex items-center justify-between h-14 px-4 border-b border-gray-200">
          <span className="text-sm font-semibold text-gray-900">YesPersonalized</span>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
            className="h-10 w-10 -mr-2 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {navList}
        {sidebarFooter}
      </aside>

      {/* Icon-only mode label */}
      {collapsed && tip && (
        <div
          role="tooltip"
          className="hidden md:block fixed left-16 z-50 -translate-y-1/2 px-2 py-1 rounded-md bg-gray-900 text-white text-xs whitespace-nowrap pointer-events-none shadow"
          style={{ top: tip.top }}
        >
          {tip.name}
        </div>
      )}

      {/* ── Main content ────────────────────────────────────────────
        * Mobile gets pt-14 so content starts below the fixed top bar.
        * Desktop has no top bar so no padding adjustment. */}
      <main className={cn('flex-1 min-w-0 flex flex-col pt-14 md:pt-0', isEditor ? 'overflow-hidden' : 'overflow-y-auto')}>
        <ScopeBanner />
        <div className={cn('flex-1', isEditor && 'min-h-0')}>{children}</div>
      </main>
    </div>
  )
}

interface PageHeaderProps {
  title: string
  description?: string
  actions?: React.ReactNode
  breadcrumbs?: Array<{ label: string; href?: string }>
}

export function PageHeader({ title: _title, description, actions, breadcrumbs }: PageHeaderProps) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 mb-2">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              {crumb.href ? (
                <Link href={crumb.href} className="hover:text-gray-900 transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-gray-900 font-medium">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      {/* Stack vertically on mobile so action buttons get full width below
        * the description, instead of crowding into one line. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
    </div>
  )
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 max-w-xs mb-6">{description}</p>
      {action}
    </div>
  )
}
